import { computed, observable, runInAction } from 'mobx';
import { Category, CoachingTip, Tag } from './';
import { hyphenate } from '../utils/string';

export const VIDEO_DETAILS_LOADED = 'video_details_loaded';

export enum VideoType {
  Premium = 'premium',
  Standard = 'standard'
}

export class Video {
  @observable public id: number;
  @observable public name?: string;
  @observable public src?: string;
  @observable public poster: string;
  @observable public description?: string;
  @observable public category?: Category;
  @observable public duration?: string;
  @observable public publishedAt: Date;
  @observable public previous?: Video;
  @observable public next?: Video;
  @observable public attachment?: string;
  @observable public coachingInformation?: string;
  @observable public coachingTips?: Array<CoachingTip>;
  @observable public views?: number;
  @observable public likes?: number;
  @observable public favourite?: boolean;
  @observable public featured?: boolean;
  @observable public recentlyAdded?: boolean;
  @observable public [VIDEO_DETAILS_LOADED]: boolean = false;
  @observable public tags?: Array<Tag>;
  @observable public type?: VideoType;

  @observable public relatedVideosFetchError: boolean = false;
  @observable public relatedVideosLoading: boolean = false;

  private _relatedVideos: Array<Video> | undefined;

  public static getIdFromRelativeRoute(relativeRoute: string): number | null {
    const [idString] = relativeRoute.match(/\d+$/) || [''];

    if (!idString) {
      return null;
    }

    return parseInt(idString, 10);
  }

  public static getFeaturedRoute(video: Video): string {
    return `/featured/${video.relativeRoute}`;
  }

  public static getRoute(video: Video): string {
    return `/videos/${video.relativeRoute}`;
  }

  constructor(
    id: number,
    name?: string,
    src?: string,
    poster: string = '',
    description?: string,
    category?: Category,
    duration?: string,
    publishedAt?: Date,
    views?: number,
    likes?: number,
    favourite?: boolean,
    previous?: Video,
    next?: Video,
    attachment?: string,
    coachingInformation?: string,
    coachingTips?: Array<CoachingTip>,
    relatedVideos?: Array<Video>,
    detailsLoaded: boolean = false,
    tags?: Array<Tag>,
    featured?: boolean,
    recentlyAdded?: boolean,
    type?: VideoType
  ) {
    runInAction(() => {
      this.id = id;
      this.name = name;
      this.src = src;
      this.poster = poster;
      this.description = description;
      this.category = category;
      this.duration = duration;
      this.publishedAt = publishedAt || new Date();
      this.previous = previous;
      this.views = views;
      this.likes = likes;
      this.favourite = favourite;
      this.next = next;
      this.attachment = attachment;
      this.coachingInformation = coachingInformation;
      this.coachingTips = coachingTips;
      this._relatedVideos = relatedVideos;
      this[VIDEO_DETAILS_LOADED] = detailsLoaded;
      this.tags = tags;
      this.featured = featured;
      this.recentlyAdded = recentlyAdded;
      this.type = type;
    });
  }

  @computed
  public get route(): string {
    return `/videos/${this.relativeRoute}`;
  }

  @computed
  public get relativeRoute(): string {
    return hyphenate(`${this.name}-${this.id}`);
  }

  @computed
  public get categoryVideos(): Array<Video> {
    return this.category ? this.category.videos || [] : [];
  }

  @computed
  public get relatedVideos(): Array<Video> {
    return (
      this._relatedVideos ||
      // HACK: For now we just use category videos which aren't us as "related"
      // TODO: Should exclude next and previous videos too...?
      this.categoryVideos
        .filter(video => video.id !== this.id)
        .sort(() => (Math.random() < 0.5 ? -1 : 1))
        .slice(0, 3)
    );
  }

  @computed
  public get rootCategory(): Category | undefined {
    return this.category
      ? this.category.parent
        ? this.category.parent
        : this.category
      : undefined;
  }

  @computed
  public get isPremium(): boolean {
    return this.type === VideoType.Premium;
  }
}

export default Video;



// WEBPACK FOOTER //
// ./src/app/models/Video.ts